import { decamelizeKeys } from "humps";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LogEventProps } from "~/common/entities/general";
import useInventory from "~/common/hooks/use-product-inventory";
import { ProductType, Topping } from "~/common/props/orchestrator";
import { useAnalytics } from "@commons-monorepo/index";
import { EVENTS } from "~/constants/events";
import { DBProduct } from "@commons-monorepo/index";
import { getDbProductId } from "~/modules/cart/utils";
import { ProductSelectorProps } from "./product-selector.types";
import { useGlobalStyles } from "~/styles/context/global-styles-wrapper";
import { useAppNavigate } from "@commons-monorepo/index";
import { useCart } from "~/modules/cart/providers/cart.provider";
import {
  getVariantByToppings,
  priceCalculator,
  priceWithWholesale,
} from "@commons-monorepo/index";
import { useOutletContext } from "@remix-run/react";

export const useProductSelector = ({
  product,
  offerId,
  selectedToppings,
  onRequiredTopping,
}: ProductSelectorProps) => {
  const { t } = useTranslation();
  const { storeMetadata } = useOutletContext<any>();
  const [logEvent] = useAnalytics({ storeMetadata });
  const { canAddProduct } = useInventory();
  const { cartProducts, addProduct } = useCart();
  const { theme } = useGlobalStyles();
  const navigate = useAppNavigate();
  const payloadTheme = decamelizeKeys({
    templateId: theme.templateId,
    headerId: theme.template.HEADER,
    bannerId: theme.template.COVER,
    categoriesId: theme.template.CATEGORIES,
    cardId: theme.template.PRODUCT_CARD,
    footerId: theme.template.FOOTER,
    storeInfoId: theme.template.STORE_INFO.SKIN,
  });

  const addCardProductFixed =
    storeMetadata.abCartAddProduct?.treatment === "on";

  const logEventFn = (eventProps: LogEventProps) => {
    logEvent(eventProps.key, eventProps.payload, eventProps.providers);
  };

  const allRequiredToppings = useMemo(() => product.toppings || [], [product]);

  const numberRequiredToppings = useMemo(
    () => allRequiredToppings.length,
    [product],
  );

  const requiredToppings = useMemo(() => {
    return (
      product.toppings?.filter(
        (topping: Topping) =>
          selectedToppings?.find((item) => topping.name === item.toppingName),
      ) || []
    );
  }, [product, selectedToppings]);

  const hasDisabled = useMemo(() => {
    return (
      (product?.toppings &&
        requiredToppings?.length < numberRequiredToppings) ||
      !canAddProduct(product, selectedToppings)
    );
  }, [product, selectedToppings, requiredToppings]);

  const productDbId = useMemo(() => {
    const variant = getVariantByToppings(
      product.productToppingVariations || [],
      selectedToppings || [],
    );
    const payload: DBProduct = {
      offerId,
      id: product.id,
      quantity: 1,
      toppings: selectedToppings || [],
      variationId: variant?.id,
      price: priceCalculator(product, selectedToppings),
      wholesalePrice: priceWithWholesale(product, selectedToppings),
      wholesale: product.wholesale,
    };
    return getDbProductId(payload);
  }, [selectedToppings, product]);

  const hasProductInTheCart = useMemo((): boolean => {
    return !!cartProducts.find((p) => p.dbId === productDbId);
  }, [cartProducts, product, selectedToppings]);

  const handleAddProduct = (productId: string) => {
    if (product.type === ProductType.Service) {
      const hasAddedService = !!cartProducts.find((cp) => cp.id === product.id);
      if (hasAddedService) return;
    }

    let variationId;
    if (product.productToppingVariations) {
      const variant = getVariantByToppings(
        product.productToppingVariations,
        selectedToppings || [],
      );
      variationId = variant?.id;
    }

    const payload: DBProduct = {
      id: productId,
      quantity: 1,
      toppings: selectedToppings || [],
      price: priceCalculator(product, selectedToppings),
      wholesalePrice: priceWithWholesale(product, selectedToppings),
      wholesale: product.wholesale,
      type: product.type,
      variationId,
      kit: !!product.kit
        ? {
            kitId: product.id,
            products: (product.products || []).map((product) => ({
              productId: product.productDetail.id,
            })),
          }
        : undefined,
    };
    if (offerId) {
      payload.offerId = offerId;
    }

    addProduct(payload);
  };

  const handleClickButton = () => {
    if (hasDisabled) {
      onRequiredTopping && onRequiredTopping();
    }
  };

  const handleBuyButtonClick = () => {
    if (!hasDisabled) {
      if (product.type === ProductType.Service) {
        logEventFn({
          key: EVENTS.SELECT_ADD_PRODUCT,
          payload: {
            ...payloadTheme,
            ...(addCardProductFixed ? { ab_test: "add_to_cart_fixed" } : {}),
          },
          providers: ["clevertap", "facebook", "appsflyer"],
        });
        const toppingsParams = (selectedToppings || [])
          .map((topping) => `${topping.toppingName}=${topping.value}`)
          .join("&");
        navigate(
          `agregar-servicio${toppingsParams ? `?${toppingsParams}` : ""}`,
        );
        return;
      }

      handleAddProduct(product.id);
      logEventFn({
        key: EVENTS.SELECT_ADD_PRODUCT,
        payload: {
          ...payloadTheme,
          ...(addCardProductFixed ? { ab_test: "add_to_cart_fixed" } : {}),
        },
        providers: ["clevertap", "facebook", "appsflyer"],
      });
    } else {
      handleClickButton();
    }
  };

  return { t, hasDisabled, hasProductInTheCart, handleBuyButtonClick };
};
